"use client";

import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { notificationsApi, type Notification } from "@/lib/api/profile";
import { cn } from "@/lib/utils/cn";

/**
 * Campana de notificaciones del navbar. Muestra un badge con el nº de no leídas
 * (polling cada 60s) y, al abrir, la lista; al hacer clic en una se marca leída.
 * Hoy la fuente principal son las alertas de cuota (80%/100%).
 */
export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const t = useTranslations("notifications");

  const { data: unread = 0 } = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: items = [] } = useQuery({
    queryKey: ["notifications-list"],
    queryFn: () => notificationsApi.list(false, 30),
    enabled: open,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["notifications-unread"] });
    qc.invalidateQueries({ queryKey: ["notifications-list"] });
  };

  const markRead = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: invalidate,
  });
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: invalidate,
  });

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("bell_aria")}
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
              aria-label={t("unread_count", { count: unread })}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-sm font-medium">{t("title")}</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t("mark_all_read")}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("empty")}
            </p>
          ) : (
            items.map((n: Notification) => (
              <button
                key={n.id}
                type="button"
                onClick={() => !n.read && markRead.mutate(n.id)}
                className={cn(
                  "flex w-full flex-col items-start gap-0.5 border-t px-3 py-2 text-left transition-colors hover:bg-accent",
                  !n.read && "bg-accent/40"
                )}
              >
                <div className="flex w-full items-start gap-2">
                  <span
                    className={cn(
                      "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                      n.level === "error"
                        ? "bg-destructive"
                        : n.level === "warning"
                        ? "bg-amber-500"
                        : "bg-primary",
                      n.read && "opacity-0"
                    )}
                  />
                  <span className="flex-1 text-sm font-medium leading-snug">
                    {n.title}
                  </span>
                </div>
                {n.body && (
                  <span className="pl-4 text-xs text-muted-foreground">
                    {n.body}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
